import { genericMemo } from "@redotech/react-util/component";
import { CustomerDataSource } from "@redotech/redo-model/marketing/segments/segment-types";
import { memo, useMemo, useState } from "react";
import {
  RedoBadge,
  RedoBadgeColor,
} from "../../arbiter-components/badge/redo-badge";
import { RedoBaseModal } from "../../arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "../../arbiter-components/modal/redo-modal";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { Category, TrifoldSelector } from "./trifold-selector";

interface SegmentFieldSelectModalProps<T extends Description> {
  isOpen: boolean;
  onModalCloseRequested: () => void;
  conditionSelected: (condition: T) => void;
  initialSelectedCondition?: T;
  categories: Category<T>[];
  title: string;
  submitText: string;
}

export const SegmentFieldSelectModal = genericMemo(
  function SegmentFieldSelectModal<T extends Description>({
    isOpen,
    onModalCloseRequested,
    conditionSelected,
    initialSelectedCondition,
    categories,
    title,
    submitText,
  }: SegmentFieldSelectModalProps<T>) {
    const [selectedCondition, setSelectedCondition] = useState<T | undefined>(
      initialSelectedCondition,
    );

    const nonEmptyCategories = useMemo(
      () => categories.filter((category) => category.items.length > 0),
      [categories],
    );

    return (
      <RedoBaseModal
        isOpen={isOpen}
        onModalCloseRequested={onModalCloseRequested}
      >
        <>
          <RedoModalHeader
            cancelClicked={onModalCloseRequested}
            title={title}
          />
          <Flex height="half-screen" overflow="hidden">
            <TrifoldSelector
              categories={nonEmptyCategories}
              selectedItem={selectedCondition}
              setSelectedItem={setSelectedCondition}
            >
              {(item) => <ConditionDescriptionPanel {...item} />}
            </TrifoldSelector>
          </Flex>
          <RedoModalFooter
            buttonPlacement="tight"
            primaryButton={{
              text: submitText,
              onClick: () => {
                if (!selectedCondition) {
                  return;
                }
                conditionSelected(selectedCondition);
              },
              disabled: !selectedCondition,
            }}
            secondaryButton={{ text: "Cancel", onClick: onModalCloseRequested }}
          />
        </>
      </RedoBaseModal>
    );
  },
);

interface Description {
  name: string;
  description: string;
  source: CustomerDataSource;
}

const ConditionDescriptionPanel = memo(function ConditionDescriptionPanel({
  name,
  description,
  source,
}: Description) {
  return (
    <>
      <Text fontSize="md" fontWeight="semibold" textColor="secondary">
        {name}
      </Text>
      <Text fontSize="xs" fontWeight="regular" textColor="secondary">
        {description}
      </Text>
      <RedoBadge
        color={sourceToBadgeColor[source]}
        size="sm"
        text={sourceToFriendlyName[source]}
      />
    </>
  );
});

const sourceToBadgeColor: Record<CustomerDataSource, RedoBadgeColor> = {
  [CustomerDataSource.REDO]: "purple",
  [CustomerDataSource.SHOPIFY]: "success",
  [CustomerDataSource.COMMENTSOLD]: "success",
};

const sourceToFriendlyName: Record<CustomerDataSource, string> = {
  [CustomerDataSource.REDO]: "Redo",
  [CustomerDataSource.SHOPIFY]: "Shopify",
  [CustomerDataSource.COMMENTSOLD]: "CommentSold",
};
